import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowRight, Calculator, ChartNoAxesCombined, Clock3, Info, TrendingUp } from 'lucide-react';
import { LangLink } from '@/i18n/LangContext';
import { swissInsuranceReference2026 as ref } from '@/data/swissInsuranceReference';

const COLORS = {
  green: '#168447',
  lightGreen: '#7fc99b',
  deep: '#183b36',
  muted: '#87a29a',
  gap: '#c98a45',
  grid: '#dce6e1',
};

const formatFrancs = (value) => `CHF ${new Intl.NumberFormat('it-CH', { maximumFractionDigits: 0 }).format(Math.round(value))}`;
const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || 0));

const coordinatedSalaryForEmployer = (salary) => {
  if (salary < ref.lppEntryThreshold) return 0;
  return Math.min(
    ref.lppMaxCoordinatedSalary,
    Math.max(ref.lppMinCoordinatedSalary, salary - ref.lppCoordinationDeduction)
  );
};

const coordinatedSalary = (totalSalary, employers) => {
  const salaryPerEmployer = totalSalary / employers;
  return coordinatedSalaryForEmployer(salaryPerEmployer) * employers;
};

const RangeField = ({ id, label, value, min, max, step, onChange, format = formatFrancs }) => (
  <label htmlFor={id} className="block">
    <span className="flex items-center justify-between gap-4 text-sm font-semibold mb-2">
      <span>{label}</span>
      <output htmlFor={id} className="text-primary tabular-nums">{format(value)}</output>
    </span>
    <input
      id={id}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
      className="w-full accent-primary"
    />
  </label>
);

const NumberField = ({ id, label, value, min = 0, max = 1000000, step = 1000, onChange }) => (
  <label htmlFor={id} className="block">
    <span className="block text-sm font-semibold mb-2">{label}</span>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">CHF</span>
      <input
        id={id}
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(clamp(event.target.value, min, max))}
        className="w-full rounded-lg border border-input bg-white py-3 pl-12 pr-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
      />
    </div>
  </label>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-white px-4 py-3 shadow-lg text-sm">
      <p className="font-semibold mb-2">{typeof label === 'number' ? formatFrancs(label) : label}</p>
      {payload.map((item) => (
        <p key={item.dataKey || item.name} className="flex items-center justify-between gap-5 text-xs text-foreground/70">
          <span>{item.name}</span>
          <strong className="text-foreground tabular-nums">{formatFrancs(item.value)}</strong>
        </p>
      ))}
    </div>
  );
};

const LppExplorer = () => {
  const [salary, setSalary] = useState(80000);
  const [employers, setEmployers] = useState(1);

  const chartData = useMemo(() => {
    const values = [];
    for (let total = 20000; total <= 120000; total += 2500) {
      values.push({
        salary: total,
        oneEmployer: coordinatedSalary(total, 1),
        twoEmployers: coordinatedSalary(total, 2),
      });
    }
    return values;
  }, []);

  const selectedCoordinated = coordinatedSalary(salary, employers);
  const outsideMandatory = Math.max(0, salary - selectedCoordinated);
  const selectedColor = employers === 1 ? COLORS.green : COLORS.gap;

  return (
    <article data-testid="lpp-explorer" className="bg-white border border-border rounded-2xl overflow-hidden">
      <div className="p-6 md:p-8 border-b border-border">
        <div className="flex items-start gap-3">
          <span className="rounded-lg bg-brand-pale p-2.5 text-primary"><ChartNoAxesCombined className="h-5 w-5" /></span>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-2">Grafico interattivo</p>
            <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Come cambia il salario coordinato LPP</h3>
          </div>
        </div>
        <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground/70 max-w-3xl">
          Sposta il salario e confronta una sola affiliazione con due impieghi di pari importo. La curva mostra la parte di reddito assicurata nel minimo LPP.
        </p>
      </div>

      <div className="grid xl:grid-cols-12">
        <div className="xl:col-span-4 p-6 md:p-8 bg-brand-pale/45 border-b xl:border-b-0 xl:border-r border-border space-y-8">
          <RangeField id="lpp-salary" label="Salario lordo annuo complessivo" value={salary} min={20000} max={120000} step={1000} onChange={setSalary} />

          <fieldset>
            <legend className="text-sm font-semibold mb-3">Come è distribuito il reddito?</legend>
            <div className="grid grid-cols-2 gap-2">
              {[1, 2].map((count) => (
                <button
                  key={count}
                  type="button"
                  aria-pressed={employers === count}
                  onClick={() => setEmployers(count)}
                  className={`rounded-lg border px-3 py-3 text-sm font-semibold transition-colors ${employers === count ? 'bg-primary border-primary text-white' : 'bg-white border-border hover:border-primary text-foreground'}`}
                >
                  {count === 1 ? 'Un datore' : 'Due datori'}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="space-y-4" aria-live="polite">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Salario coordinato stimato</p>
              <p data-testid="lpp-coordinated-result" className="font-heading text-3xl font-bold text-primary tabular-nums">{formatFrancs(selectedCoordinated)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Parte non compresa nel minimo LPP</p>
              <p className="font-heading text-xl font-bold tabular-nums">{formatFrancs(outsideMandatory)}</p>
            </div>
          </div>
        </div>

        <div className="xl:col-span-8 p-4 sm:p-6 md:p-8 min-w-0">
          <div className="h-[330px] md:h-[390px]" role="img" aria-label="Grafico a linee del salario coordinato LPP in funzione del salario lordo per uno o due datori di lavoro.">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 20, right: 18, left: 4, bottom: 18 }}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="salary"
                  type="number"
                  domain={[20000, 120000]}
                  tickFormatter={(value) => `${Math.round(value / 1000)}k`}
                  tick={{ fontSize: 11 }}
                  label={{ value: 'Salario lordo annuo (CHF)', position: 'insideBottom', offset: -10, fontSize: 12 }}
                />
                <YAxis
                  width={58}
                  tickFormatter={(value) => `${Math.round(value / 1000)}k`}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend verticalAlign="top" height={38} />
                <Line type="monotone" dataKey="oneEmployer" name="Un datore" stroke={COLORS.green} strokeWidth={employers === 1 ? 4 : 2} dot={false} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="twoEmployers" name="Due datori" stroke={COLORS.gap} strokeWidth={employers === 2 ? 4 : 2} dot={false} activeDot={{ r: 5 }} />
                <ReferenceLine x={salary} stroke={COLORS.deep} strokeDasharray="5 5" />
                <ReferenceDot x={salary} y={selectedCoordinated} r={6} fill={selectedColor} stroke="#fff" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            Modello semplificato con reddito diviso in parti uguali tra due datori. Le casse possono adeguare la deduzione al grado d’occupazione o prevedere prestazioni sovraobbligatorie.
          </p>
        </div>
      </div>
    </article>
  );
};

const GapCalculator = () => {
  const [income, setIncome] = useState(85000);
  const [targetIncome, setTargetIncome] = useState(68000);
  const [avsPension, setAvsPension] = useState(24000);
  const [lppPension, setLppPension] = useState(24000);
  const [privateCapital, setPrivateCapital] = useState(50000);
  const [retirementYears, setRetirementYears] = useState(20);
  const [currentAge, setCurrentAge] = useState(40);
  const [retirementAge, setRetirementAge] = useState(65);

  const safeTargetIncome = Math.min(targetIncome, income);
  const annualPension = avsPension + lppPension;
  const annualGap = Math.max(0, safeTargetIncome - annualPension);
  const grossCapitalGap = annualGap * retirementYears;
  const residualCapitalGap = Math.max(0, grossCapitalGap - privateCapital);
  const savingMonths = Math.max(12, (retirementAge - currentAge) * 12);
  const monthlySaving = residualCapitalGap / savingMonths;
  const coveredRatio = safeTargetIncome > 0 ? Math.min(100, (annualPension / safeTargetIncome) * 100) : 100;
  const gapRatio = Math.max(0, 100 - coveredRatio);

  const barData = [
    { name: 'Fabbisogno desiderato', value: safeTargetIncome, color: COLORS.deep },
    { name: 'Rendite previste', value: annualPension, color: COLORS.green },
    { name: 'Lacuna annua', value: annualGap, color: COLORS.gap },
  ];

  const capitalStoryData = [
    { name: 'Capitale da trovare', value: grossCapitalGap, color: COLORS.gap },
    { name: 'Capitale già disponibile', value: privateCapital, color: COLORS.lightGreen },
    { name: 'Ancora da costruire', value: residualCapitalGap, color: COLORS.deep },
  ];

  return (
    <article data-testid="pension-gap-calculator" className="bg-white border border-border rounded-2xl overflow-hidden">
      <div className="p-6 md:p-8 border-b border-border bg-gradient-to-br from-brand-pale/70 via-white to-white">
        <div className="flex items-start gap-3">
          <span className="rounded-lg bg-primary p-2.5 text-white"><Calculator className="h-5 w-5" /></span>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-2">Calcolatore emozionale</p>
            <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Stima la tua possibile lacuna previdenziale</h3>
          </div>
        </div>
        <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground/70 max-w-3xl">
          Non è solo un numero: è la differenza tra il reddito che immagini e quello che potresti ricevere. Sposta i cursori e guarda quanto può mancare ogni anno.
        </p>
      </div>

      <div className="grid xl:grid-cols-12">
        <div className="xl:col-span-5 p-6 md:p-8 bg-brand-pale/35 border-b xl:border-b-0 xl:border-r border-border">
          <div className="space-y-7">
            <RangeField id="gap-income" label="Salario annuo lordo / riferimento" value={income} min={20000} max={300000} step={1000} onChange={(value) => {
              setIncome(value);
              setTargetIncome((current) => Math.min(current, value));
            }} />
            <RangeField id="gap-target" label="Fabbisogno desiderato in pensione" value={safeTargetIncome} min={0} max={income} step={1000} onChange={setTargetIncome} />

            <div className="grid sm:grid-cols-2 gap-4">
              <NumberField id="gap-avs" label="Rendita AVS annua prevista" value={avsPension} max={60000} onChange={setAvsPension} />
              <NumberField id="gap-lpp" label="Rendita LPP annua prevista" value={lppPension} max={150000} onChange={setLppPension} />
              <NumberField id="gap-private" label="Capitale privato già disponibile" value={privateCapital} max={3000000} onChange={setPrivateCapital} />
            </div>

            <fieldset className="rounded-xl border border-border bg-white/70 p-4">
              <legend className="px-2 text-sm font-semibold text-foreground">Tempo a disposizione</legend>
              <div className="grid sm:grid-cols-3 gap-4 items-end">
                <RangeField id="gap-current-age" label="Età oggi" value={currentAge} min={18} max={64} step={1} onChange={(value) => setCurrentAge(Math.min(value, retirementAge - 1))} format={(value) => `${value} anni`} />
                <RangeField id="gap-retirement-age" label="Età pensione" value={retirementAge} min={58} max={70} step={1} onChange={(value) => setRetirementAge(Math.max(value, currentAge + 1))} format={(value) => `${value} anni`} />
                <RangeField id="gap-years" label="Anni da finanziare" value={retirementYears} min={10} max={30} step={1} onChange={setRetirementYears} format={(value) => `${value} anni`} />
              </div>
            </fieldset>
          </div>
        </div>

        <div className="xl:col-span-7 p-5 sm:p-6 md:p-8 min-w-0">
          <div className="grid sm:grid-cols-3 gap-3 mb-8" aria-live="polite">
            <div className="rounded-xl bg-brand-deep text-white p-5 sm:col-span-2">
              <p className="text-xs text-white/65 mb-2">Ogni anno potrebbero mancare</p>
              <p data-testid="annual-gap-result" className="font-heading text-3xl md:text-5xl font-bold tabular-nums text-brand-light">{formatFrancs(annualGap)}</p>
              <p className="mt-3 text-xs text-white/65">Su {retirementYears} anni diventano {formatFrancs(grossCapitalGap)} prima di considerare il capitale già disponibile.</p>
            </div>
            <div className="rounded-xl border border-primary/25 bg-white p-5">
              <p className="text-xs text-foreground/60 mb-2">Da accantonare al mese</p>
              <p data-testid="monthly-saving-result" className="font-heading text-2xl md:text-3xl font-bold tabular-nums text-primary">{formatFrancs(monthlySaving)}</p>
              <p className="mt-3 text-xs text-foreground/55">Senza rendimento, da oggi alla pensione.</p>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-brand-pale/50 p-5 md:p-6 mb-7">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-2">Impatto immediato</p>
                <h4 className="font-heading text-xl md:text-2xl font-bold tracking-tight">Il tuo reddito desiderato non è coperto al {gapRatio.toFixed(0)}%.</h4>
              </div>
              <p className="font-heading text-4xl font-bold text-primary tabular-nums">{coveredRatio.toFixed(0)}%</p>
            </div>
            <div className="h-4 rounded-full bg-white overflow-hidden border border-border" aria-hidden="true">
              <div className="h-full bg-primary" style={{ width: `${coveredRatio}%` }} />
            </div>
            <div className="mt-2 flex justify-between text-[11px] text-muted-foreground">
              <span>Rendite previste</span>
              <span>Lacuna da coprire</span>
            </div>
          </div>

          <div className="h-[310px] md:h-[350px]" role="img" aria-label="Grafico a barre del fabbisogno pensionistico desiderato, delle rendite previste e della lacuna annuale stimata.">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} layout="vertical" margin={{ top: 10, right: 22, left: 8, bottom: 8 }}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" domain={[0, Math.max(income, safeTargetIncome, annualPension)]} tickFormatter={(value) => `${Math.round(value / 1000)}k`} tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" width={118} tick={{ fontSize: 11 }} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="value" name="Importo annuo" radius={[0, 5, 5, 0]}>
                  {barData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-7 h-[250px]" role="img" aria-label="Grafico del capitale necessario per coprire la lacuna previdenziale stimata.">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={capitalStoryData} margin={{ top: 10, right: 18, left: 0, bottom: 32 }}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-8} textAnchor="end" height={54} />
                <YAxis tickFormatter={(value) => `${Math.round(value / 1000)}k`} tick={{ fontSize: 11 }} width={58} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="value" name="Capitale" radius={[6, 6, 0, 0]}>
                  {capitalStoryData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-6 border-l-2 border-brand-light pl-4 text-xs leading-relaxed text-muted-foreground space-y-2">
            <p>Formula semplificata: (fabbisogno annuo desiderato − AVS − LPP) × anni da finanziare − capitale privato disponibile.</p>
            <p>Non considera inflazione, imposte, rendimento, aliquote di conversione o struttura familiare. Usa i dati del certificato LPP e una stima AVS ufficiale per un risultato più significativo.</p>
          </div>

          <LangLink to="/contatti" className="mt-7 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-white hover:bg-brand-dark transition-colors">
            Fai verificare la tua lacuna <ArrowRight className="h-4 w-4" />
          </LangLink>
        </div>
      </div>
    </article>
  );
};

const InvestmentTimingCalculator = () => {
  const [monthlyAmount, setMonthlyAmount] = useState(500);
  const [earlyAge, setEarlyAge] = useState(30);
  const [lateAge, setLateAge] = useState(40);
  const [retirementAge, setRetirementAge] = useState(65);
  const [annualReturn, setAnnualReturn] = useState(4);

  const futureValue = (startAge) => {
    const months = Math.max(0, (retirementAge - startAge) * 12);
    const monthlyRate = annualReturn / 100 / 12;
    if (months <= 0) return 0;
    if (monthlyRate === 0) return monthlyAmount * months;
    return monthlyAmount * (((1 + monthlyRate) ** months - 1) / monthlyRate);
  };

  const earlyCapital = futureValue(earlyAge);
  const lateCapital = futureValue(lateAge);
  const difference = Math.max(0, earlyCapital - lateCapital);
  const yearsDifference = Math.max(0, lateAge - earlyAge);
  const chartData = [
    { name: `Inizio a ${earlyAge} anni`, value: earlyCapital, color: COLORS.green },
    { name: `Inizio a ${lateAge} anni`, value: lateCapital, color: COLORS.gap },
    { name: 'Vantaggio del tempo', value: difference, color: COLORS.deep },
  ];

  return (
    <article data-testid="investment-timing-calculator" className="bg-white border border-border rounded-2xl overflow-hidden">
      <div className="p-6 md:p-8 border-b border-border">
        <div className="flex items-start gap-3">
          <span className="rounded-lg bg-brand-pale p-2.5 text-primary"><TrendingUp className="h-5 w-5" /></span>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-2">Tempo e rendimento</p>
            <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Quanto pesa iniziare prima?</h3>
          </div>
        </div>
        <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground/70 max-w-3xl">
          Confronta lo stesso versamento mensile iniziato a due età diverse. Il grafico rende visibile il costo dell’attesa.
        </p>
      </div>

      <div className="grid xl:grid-cols-12">
        <div className="xl:col-span-4 p-6 md:p-8 bg-brand-pale/35 border-b xl:border-b-0 xl:border-r border-border space-y-7">
          <RangeField id="timing-monthly" label="Versamento mensile" value={monthlyAmount} min={50} max={3000} step={50} onChange={setMonthlyAmount} />
          <RangeField id="timing-early" label="Prima età di inizio" value={earlyAge} min={18} max={64} step={1} onChange={(value) => {
            setEarlyAge(Math.min(value, lateAge - 1));
          }} format={(value) => `${value} anni`} />
          <RangeField id="timing-late" label="Seconda età di inizio" value={lateAge} min={19} max={65} step={1} onChange={(value) => {
            setLateAge(Math.max(value, earlyAge + 1));
          }} format={(value) => `${value} anni`} />
          <RangeField id="timing-retirement" label="Età obiettivo" value={retirementAge} min={50} max={70} step={1} onChange={(value) => setRetirementAge(Math.max(value, lateAge + 1))} format={(value) => `${value} anni`} />
          <RangeField id="timing-return" label="Rendimento annuo ipotizzato" value={annualReturn} min={0} max={8} step={0.25} onChange={setAnnualReturn} format={(value) => `${value.toFixed(2)}%`} />
        </div>

        <div className="xl:col-span-8 p-5 sm:p-6 md:p-8 min-w-0">
          <div className="grid md:grid-cols-3 gap-3 mb-8" aria-live="polite">
            <div className="rounded-xl bg-brand-deep text-white p-5 md:col-span-2">
              <p className="text-xs text-white/65 mb-2">Vantaggio di iniziare {yearsDifference} anni prima</p>
              <p className="font-heading text-3xl md:text-5xl font-bold text-brand-light tabular-nums">{formatFrancs(difference)}</p>
            </div>
            <div className="rounded-xl border border-primary/25 bg-white p-5">
              <p className="text-xs text-foreground/60 mb-2">Stesso importo mensile</p>
              <p className="font-heading text-2xl md:text-3xl font-bold tabular-nums text-primary">{formatFrancs(monthlyAmount)}</p>
              <p className="mt-3 text-xs text-foreground/55">Il tempo lavora solo se lo attivi.</p>
            </div>
          </div>

          <div className="h-[320px] md:h-[380px]" role="img" aria-label="Grafico a barre che confronta il capitale finale se si inizia a investire a due età diverse.">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 18, left: 0, bottom: 40 }}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-10} textAnchor="end" height={62} />
                <YAxis tickFormatter={(value) => `${Math.round(value / 1000)}k`} tick={{ fontSize: 11 }} width={58} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="value" name="Capitale stimato" radius={[6, 6, 0, 0]}>
                  {chartData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
            <Clock3 className="h-4 w-4 shrink-0 mt-0.5" />
            Simulazione matematica con versamenti mensili costanti e rendimento annuo ipotizzato. Non rappresenta una garanzia di rendimento o una raccomandazione d’investimento.
          </p>
        </div>
      </div>
    </article>
  );
};


export const PensionLab = ({ section }) => (
  <div>
    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-balance">{section.title}</h2>
    {section.lead && <p className="mt-5 max-w-3xl text-base md:text-lg leading-relaxed text-foreground/70">{section.lead}</p>}
    <div className="mt-9 space-y-6 md:space-y-8">
      <LppExplorer />
      <GapCalculator />
      <InvestmentTimingCalculator />
    </div>
  </div>
);
